import React, {useState} from 'react';
import {StyleSheet} from 'react-native';
import {useMutation} from '@tanstack/react-query';
import {useTranslation} from 'react-i18next';

import {fetchDeviceLanKey} from '@/entities/device/api';
import {ApiError} from '@/shared/api';
import {Banner} from '@/shared/ui/banner';
import {AppButton} from '@/shared/ui/button';
import {Card} from '@/shared/ui/card';
import {Field} from '@/shared/ui/field';
import {AppText} from '@/shared/ui/text';
import {spacing} from '@/shared/ui/theme';

/**
 * The key a desktop bridge presents to this terminal's own HTTP API on the LAN.
 *
 * ## Why it is a button and not a field
 *
 * This value opens a door. `GET /control/devices/{id}/lan-key` is gated on
 * `devices/manage` rather than the `read` the rest of the card takes, precisely so
 * that looking at a terminal and taking its keys are not the same right — and a screen
 * that fetched it on mount would hand it to every viewer of the card and put it in
 * every query cache and every crash dump along the way. So: nothing is requested until
 * somebody asks, and what comes back is shown once and dropped when the screen closes.
 * It is never written to a log and never appears in the device list.
 *
 * A member who can see this terminal may still get a 403 here. That is the route
 * working, not a fault, and the message says so rather than offering a retry.
 *
 * ## `null` is not "no key"
 *
 * Only 2026-08-20 firmware and later report the key upward; everything older answers
 * null forever. Drawing that as «ключа нет» would tell an owner their bridge cannot
 * work when in truth nobody has ever asked the board. The empty answer therefore reads
 * «терминал ещё не сообщил» and points at the firmware, which is the actionable half.
 */
const styles = StyleSheet.create({
  card: {gap: spacing(3)},
});

export function LanKeyCard({
  companyId,
  deviceId,
}: {
  companyId: string;
  deviceId: string;
}) {
  const {t} = useTranslation();
  const [revealed, setRevealed] = useState<string | null>(null);
  const [asked, setAsked] = useState(false);

  const reveal = useMutation({
    mutationFn: () => fetchDeviceLanKey(companyId, deviceId),
    onSuccess: key => {
      setAsked(true);
      setRevealed(key);
    },
  });

  const errorOf = (e: unknown): string =>
    e instanceof ApiError
      ? e.status === 403
        ? t('lanKey.forbidden')
        : e.message
      : t('errors.generic');

  return (
    <Card padding={4} style={styles.card}>
      <AppText variant="caption">{t('lanKey.hint')}</AppText>

      {reveal.isError ? <Banner tone="error" title={errorOf(reveal.error)} /> : null}

      {asked && revealed ? (
        <>
          <Field label={t('lanKey.value')} value={revealed} mono />
          <Banner tone="warning" title={t('lanKey.once')} />
          <AppButton
            title={t('lanKey.conceal')}
            tone="ghost"
            size="sm"
            onPress={() => {
              setRevealed(null);
              setAsked(false);
              reveal.reset();
            }}
            testID="lan-key-conceal"
          />
        </>
      ) : asked ? (
        // Answered, with nothing in it: the terminal has not reported one yet.
        <Banner tone="info" title={t('lanKey.notReported')} message={t('lanKey.notReportedHint')} />
      ) : (
        <AppButton
          title={t('lanKey.reveal')}
          tone="neutral"
          size="sm"
          loading={reveal.isPending}
          onPress={() => reveal.mutate()}
          testID="lan-key-reveal"
        />
      )}
    </Card>
  );
}
